import {
  Box,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  Text,
  Badge,
  VStack,
  HStack,
  Button,
  Image,
  Divider,
  useColorModeValue,
} from "@chakra-ui/react";
import embedded from "../../assets/blog_images/embedded/wix_page_booking.png";
import website from "../../assets/images/lumiere-szalon-classic_web.png";
import { FaMoneyBill } from "react-icons/fa";


// ── Icons (inline SVG as React components) ────────────────────────────────────

const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const CodeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const BellIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const StarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const BarChartIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="20" x2="12" y2="10" />
    <line x1="18" y1="20" x2="18" y2="4" />
    <line x1="6" y1="20" x2="6" y2="16" />
  </svg>
);

const PaletteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/>
    <circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/>
    <circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/>
    <circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/>
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>
  </svg>
);

const ZapIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const UsersIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

// ── Types ─────────────────────────────────────────────────────────────────────

interface Feature {
  label: string;
  icon: React.ReactNode;
}

interface ProductCard {
  tag: string;
  tagColor: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  description: string;
  features: Feature[];
  cta: string;
  accent: string;
  accentLight: string;
  /** Path or URL to the card header image */
  imageSrc: string;
  imageAlt: string;
  href: string;
}

// ── Product cards data ────────────────────────────────────────────────────────

const products: ProductCard[] = [
  {
    tag: "Teljes csomag",
    tagColor: "teal",
    icon: <GlobeIcon />,
    title: "Weboldal + foglalási rendszer",
    subtitle: "Professzionális megjelenés, azonnal",
    description:
      "Kapj egy teljesen felépített, mobilbarát weboldalt, amely egyszerre mutatja be a vállalkozásodat és lehetővé teszi az online időpontfoglalást - egyetlen rendszerben, technikai tudás nélkül.",
    features: [
      { label: "Testreszabható design, saját logó és színek", icon: <PaletteIcon /> },
      { label: "Szolgáltatások, árak, galéria, rólunk oldal", icon: <StarIcon /> },
      { label: "Hozzá tartozó foglalási rendszer", icon: <BellIcon /> },
      { label: "Google naptár szinkronizáció", icon: <CalendarIcon /> },
      { label: "Vendégértékelések megjelenítése", icon: <UsersIcon /> },
      { label: "SSL, tárhelyszolgáltatás, saját domain beállítása", icon: <ShieldIcon /> },
    ],
    cta: "Megnézem a weboldalt",
    accent: "#1a7a6e",
    accentLight: "rgba(26,122,110,0.08)",
    imageSrc: website,
    imageAlt: "Wobble weboldal előnézet időpontfoglalóval",
    href: "https://wobble.hu/weboldal",
  },
  {
    tag: "Legnépszerűbb",
    tagColor: "green",
    icon: <CalendarIcon />,
    title: "Online időpontfoglaló rendszer",
    subtitle: "Foglalások éjjel-nappal, automatikusan",
    description:
      "Ha csak egy szép foglalási oldalt szeretnél, ahol az ügyfeleid önállóan foglalnak időpontot - te pedig valós idejű értesítéseket kapsz, kezeled a naptáradat és csökkented a lemondásokat automatikus emlékeztetőkkel.",
    features: [
      { label: "24/7 online foglalás, mobilon is", icon: <ZapIcon /> },
      { label: "Egyedi foglalási design", icon: <UsersIcon /> },
      { label: "Várólista beállítása", icon: <CalendarIcon /> },
      { label: "Kérdőív már foglaláskor", icon: <BarChartIcon /> },
      { label: "Ügyfélfiók a könnyebb foglalásokért", icon: <GlobeIcon /> },
      { label: "Előlegfizetés (Stripe)", icon: <FaMoneyBill /> },
    ],
    cta: "Megnézem a rendszert",
    accent: "#0d9461",
    accentLight: "rgba(13,148,97,0.08)",
    imageSrc: "../assets/mockup_moov_studio.png",
    imageAlt: "Wobble online időpontfoglaló rendszer",
    href: "https://wobble.hu/online-idopontfoglalo",
  },
  {
    tag: "Van weboldalad?",
    tagColor: "blue",
    icon: <CodeIcon />,
    title: "Beágyazható foglalási oldal",
    subtitle: "Egy sor kód — teljes funkcionalitás",
    description:
      "Ha már van saját weboldalad, egyszerűen illeszd be a Wobblet. Néhány perc alatt elérhető - az összes foglalás, emlékeztető és naptár azonnal működik.",
    features: [
      { label: "1 soros script beillesztés, bármelyik CMS-be", icon: <CodeIcon /> },
      { label: "Teljesen testreszabható design (CSS override)", icon: <PaletteIcon /> },
      { label: "WordPress, Wix, Squarespace vagy egyedi oldalba", icon: <GlobeIcon /> },
      { label: "Nem lassítja a weboldaladat (async betöltés)", icon: <ZapIcon /> },
      { label: "Foglalási oldal ugyanúgy elérhető", icon: <MapPinIcon /> },

    ],
    cta: "Megnézem a beágyazás lehetőséget",
    accent: "#2563eb",
    accentLight: "rgba(37,99,235,0.08)",
    imageSrc: embedded,
    imageAlt: "Wobble beágyazható foglalási widget meglévő weboldalba",
    href: "https://wobble.hu/beagyazas",
  },
];

// ── Main component ────────────────────────────────────────────────────────────

export default function WobbleFeatures() {
  const sectionBg = useColorModeValue("#f8faf9", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const textMuted = useColorModeValue("gray.500", "gray.400");

  return (
    <Box
      bg={sectionBg}
      py={{ base: 16, md: 24 }}
      px={{ base: 4, md: 8 }}
      position="relative"
      overflow="hidden"
    >
      {/* Background decoration */}
      <Box
        position="absolute"
        top="-120px"
        right="-120px"
        w="500px"
        h="500px"
        borderRadius="full"
        bg="radial-gradient(circle, rgba(26,122,110,0.06) 0%, transparent 70%)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom="-80px"
        left="-80px"
        w="400px"
        h="400px"
        borderRadius="full"
        bg="radial-gradient(circle, rgba(13,148,97,0.05) 0%, transparent 70%)"
        pointerEvents="none"
      />

      <Container maxW="1200px" position="relative">
        {/* Header */}
        <VStack spacing={4} mb={{ base: 12, md: 16 }} textAlign="center">
          <Badge
            bg="rgba(26,122,110,0.1)"
            color="#1a7a6e"
            px={4}
            py={1.5}
            borderRadius="full"
            fontSize="xs"
            fontWeight="700"
            letterSpacing="0.08em"
            textTransform="uppercase"
          >
            Wobble rendszer
          </Badge>
          <Heading
            as="h2"
            fontSize={{ base: "3xl", md: "5xl" }}
            fontWeight="800"
            color="gray.800"
            lineHeight="1.1"
            letterSpacing="-0.02em"
          >
            Minden, ami kell a növekedéshez
          </Heading>
          <Text
            fontSize={{ base: "md", md: "lg" }}
            color={textMuted}
            maxW="560px"
            lineHeight="1.7"
          >
            Válassz a három megoldás közül — mindegyik azonnal élőbe lép, technikai háttér
            nélkül, és naprakészen tartja a naptáradat.
          </Text>
        </VStack>

        {/* Cards */}
        <Grid
          templateColumns={{ base: "1fr", lg: "repeat(3, 1fr)" }}
          gap={{ base: 6, md: 8 }}
          alignItems="start"
        >
          {products.map((product, idx) => (
            <GridItem key={idx}>
              <Box
                bg={cardBg}
                borderRadius="24px"
                overflow="hidden"
                border="1px solid"
                borderColor="rgba(0,0,0,0.06)"
                boxShadow="0 4px 24px rgba(0,0,0,0.06)"
                transition="transform 0.2s ease, box-shadow 0.2s ease"
                _hover={{
                  transform: "translateY(-4px)",
                  boxShadow: `0 16px 48px rgba(0,0,0,0.12), 0 0 0 1px ${product.accent}22`,
                }}
                h="full"
                display="flex"
                flexDirection="column"
              >
                {/* Card image header */}
                <Box
                  position="relative"
                  overflow="hidden"
                  borderBottom="1px solid"
                  borderColor="rgba(0,0,0,0.05)"
                >
                  <Image
                    src={product.imageSrc}
                    alt={product.imageAlt}
                    w="full"
                    h={{ base: "200px", md: "220px" }}
                    objectFit="cover"
                    objectPosition="top center"
                    display="block"
                    fallback={
                      <Box
                        w="full"
                        h={{ base: "200px", md: "220px" }}
                        bg={product.accentLight}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Box color={product.accent} opacity={0.3}>
                          {product.icon}
                        </Box>
                      </Box>
                    }
                  />
                  {/* Subtle gradient overlay at the bottom edge */}
                  <Box
                    position="absolute"
                    bottom={0}
                    left={0}
                    right={0}
                    h="48px"
                    bgGradient="linear(to-t, whiteAlpha.600, transparent)"
                    pointerEvents="none"
                  />
                  <Badge
                    position="absolute"
                    top={3}
                    right={3}
                    bg={product.accent}
                    color="white"
                    fontSize="10px"
                    fontWeight="700"
                    px={2.5}
                    py={1}
                    borderRadius="full"
                    letterSpacing="0.04em"
                    boxShadow="0 2px 8px rgba(0,0,0,0.2)"
                  >
                    {product.tag}
                  </Badge>
                </Box>

                {/* Content */}
                <Box p={{ base: 5, md: 6 }} flex="1" display="flex" flexDirection="column">
                  {/* Title block */}
                  <HStack spacing={2.5} mb={3} align="center">
                    <Box
                      color={product.accent}
                      bg={product.accentLight}
                      p={2}
                      borderRadius="10px"
                    >
                      {product.icon}
                    </Box>
                    <VStack spacing={0} align="start">
                      <Text fontSize="xs" color={textMuted} fontWeight="500">
                        {product.subtitle}
                      </Text>
                    </VStack>
                  </HStack>

                  <Heading
                    as="h3"
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="800"
                    color="gray.800"
                    mb={3}
                    lineHeight="1.2"
                    letterSpacing="-0.02em"
                  >
                    {product.title}
                  </Heading>

                  <Text
                    fontSize="sm"
                    color={textMuted}
                    lineHeight="1.7"
                    mb={5}
                  >
                    {product.description}
                  </Text>

                  <Divider mb={5} borderColor="gray.100" />

                  {/* Features */}
                  <VStack spacing={2.5} align="stretch" mb={6} flex="1">
                    {product.features.map((feat, fi) => (
                      <HStack key={fi} spacing={3} align="start">
                        <Box
                          color={product.accent}
                          flexShrink={0}
                          mt="1px"
                          opacity={0.9}
                        >
                          {feat.icon}
                        </Box>
                        <Text fontSize="sm" color="gray.700" lineHeight="1.5">
                          {feat.label}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>

                  {/* CTA */}
                  <Button
                    as="a"
                    href={product.href}
                    size="md"
                    bg={product.accent}
                    color="white"
                    borderRadius="12px"
                    fontWeight="700"
                    fontSize="sm"
                    px={6}
                    py={5}
                    rightIcon={<ArrowIcon />}
                    _hover={{
                      bg: product.accent,
                      opacity: 0.9,
                      transform: "translateX(2px)",
                    }}
                    transition="all 0.2s"
                    w="full"
                  >
                    {product.cta}
                  </Button>
                </Box>
              </Box>
            </GridItem>
          ))}
        </Grid>

        {/* Bottom trust bar */}
        <Box mt={{ base: 12, md: 16 }}>
          <Flex
            justify="center"
            align="center"
            gap={{ base: 6, md: 10 }}
            flexWrap="wrap"
          >
            {[
              { value: "10+", label: "féle vállalkozás" },
              { value: "98%", label: "elégedett ügyfél" },
              { value: "< 30 perc", label: "beállítási idő" },
              { value: "24/7", label: "automatikus foglalás" },
            ].map((stat, i) => (
              <Box key={i} textAlign="center">
                <Text
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="800"
                  color="gray.800"
                  letterSpacing="-0.03em"
                >
                  {stat.value}
                </Text>
                <Text fontSize="xs" color={textMuted} fontWeight="500">
                  {stat.label}
                </Text>
              </Box>
            ))}
          </Flex>
        </Box>
      </Container>
    </Box>
  );
}